import React, { useContext, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Empty,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Upload,
  Modal,
  message,
} from 'antd';
import {
  DeleteOutlined,
  QuestionCircleOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import axios from 'axios';
import { Context } from '../../context/context';
import CustomModal from '../../components/modal';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { disableRefetch } from '../../redux/slices/menu';

export default function HomepageProducts() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { setIsModalVisible } = useContext(Context);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const { activeMenu } = useSelector((state) => state.menu, shallowEqual);

  // Image upload states
  const [currentProductId, setCurrentProductId] = useState(null);
  const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [homepageProductImages, setHomepageProductImages] = useState(() => {
    // Try to get stored image URLs from localStorage
    const storedImages = localStorage.getItem('homepageProductImages');
    return storedImages ? JSON.parse(storedImages) : {};
  });

  const fetchHomepageProducts = async () => {
    setLoading(true);
    try {
      console.log('Fetching homepage products from API...');
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/v1/dashboard/admin/homepage-products-list`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      // Log the API response for debugging
      console.log('Homepage Products API response status:', response.status);

      // Format the data for display
      let formattedData = [];

      // Detailed logging of product IDs
      if (response.data && Array.isArray(response.data.products)) {
        console.log(`Found ${response.data.products.length} homepage products`);

        // Log a sample product to examine all available fields
        if (response.data.products.length > 0) {
          console.log(
            'Sample product object structure:',
            response.data.products[0]
          );

          // Log all product IDs to help debug deletion issues
          const idList = response.data.products.map((p) => ({
            id: p.id,
            product_id: p.product_id,
            category: p.product_cat,
          }));
          console.log('All homepage product IDs:', idList);
        }

        // Apply stored images to products from localStorage
        formattedData = response.data.products.map((product) => {
          if (homepageProductImages[product.id]) {
            return {
              ...product,
              product_cardImg: homepageProductImages[product.id],
            };
          }
          return product;
        });
      } else {
        console.log(
          'No homepage products found or invalid API response format'
        );
      }

      console.log('Formatted data:', formattedData);
      setProducts(formattedData);
    } catch (error) {
      console.error('Error fetching homepage products:', error);
      toast.error('Failed to fetch homepage products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHomepageProducts();
  }, []);

  // Save homepage product images to localStorage when they change
  useEffect(() => {
    localStorage.setItem(
      'homepageProductImages',
      JSON.stringify(homepageProductImages)
    );
  }, [homepageProductImages]);

  useEffect(() => {
    if (activeMenu.refetch) {
      fetchHomepageProducts();
      dispatch(disableRefetch(activeMenu));
    }
  }, [activeMenu.refetch]);

  const handleDeleteProduct = async () => {
    if (!selectedProduct) return;

    try {
      // Log detailed information about the product being deleted
      console.log('Attempting to delete homepage product:', {
        id: selectedProduct.id,
        product_id: selectedProduct.product_id,
        product_cat: selectedProduct.product_cat,
        fullObject: selectedProduct,
      });

      // Products might be deleted by product_id instead of id
      // Using product_id if available, otherwise use id
      const deleteId = selectedProduct.product_id || selectedProduct.id;

      if (!deleteId) {
        toast.error('No valid ID found for deletion');
        return;
      }

      console.log(`Using ID ${deleteId} for deletion request`);

      // Make API request for deletion
      const response = await axios.delete(
        `${process.env.REACT_APP_BASE_URL}/api/v1/dashboard/admin/homepage-products/delete/${deleteId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );

      console.log('Delete API response:', response.data);

      if (response.status === 200) {
        toast.success('Product removed from homepage successfully');
        setIsModalVisible(false);
        fetchHomepageProducts();
      } else {
        toast.error(
          response.data.message || 'Failed to remove product from homepage'
        );
      }
    } catch (error) {
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        message: error.response?.data?.message,
        data: error.response?.data,
      });

      // Check for specific "not found" error message
      if (error.response?.data?.message?.includes('not found')) {
        toast.error('Product not found. It may have been already removed.');
        // Refresh the product list to ensure UI is in sync with server
        setIsModalVisible(false);
        fetchHomepageProducts();
      } else {
        toast.error(
          error.response?.data?.message ||
            'Failed to remove product from homepage'
        );
      }
    }
  };

  // Image upload handlers
  const handleUploadClick = (productId) => {
    setCurrentProductId(productId);
    setSelectedFile(null);
    setPreviewImage(null);
    setIsUploadModalVisible(true);
  };

  const handleUploadCancel = () => {
    setIsUploadModalVisible(false);
    setSelectedFile(null);
    setPreviewImage(null);
  };

  const handleFileSelect = (file) => {
    if (file) {
      setSelectedFile(file);
      // Preview the selected image
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
    return false; // Prevent automatic upload
  };

  const handleHomepageImageUpload = async () => {
    if (!selectedFile || !currentProductId) {
      message.error('Please select an image file');
      return;
    }

    setUploadLoading(true);
    const formData = new FormData();
    formData.append('image', selectedFile);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/v1/dashboard/admin/homepage-products/${currentProductId}/upload`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      console.log('Homepage Upload response:', data);

      // Update the image in the UI and localStorage
      setHomepageProductImages((prev) => ({
        ...prev,
        [currentProductId]: data.image_url,
      }));

      // Update the products data state
      setProducts((prev) =>
        prev.map((product) =>
          product.id === currentProductId
            ? { ...product, product_cardImg: data.image_url }
            : product
        )
      );

      toast.success('Homepage product image uploaded successfully');
      setIsUploadModalVisible(false);
    } catch (error) {
      console.error('Error uploading homepage product image:', error);
      toast.error('Failed to upload homepage product image');
    } finally {
      setUploadLoading(false);
    }
  };

  // Helper function to get color for category tag
  const getCategoryColor = (category) => {
    switch (category.toLowerCase()) {
      case 'popular':
        return 'red';
      case 'fashion':
        return 'blue';
      case 'food':
        return 'green';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: t('ID'),
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: t('Product Name'),
      dataIndex: 'product_name',
      key: 'product_name',
    },
    {
      title: t('Product ID'),
      dataIndex: 'product_id',
      key: 'product_id',
    },
    {
      title: () => (
        <Tooltip title='Category on the homepage: Popular, Fashion, or Food'>
          <Space>
            {t('Homepage Category')}
            <QuestionCircleOutlined />
          </Space>
        </Tooltip>
      ),
      dataIndex: 'product_cat',
      key: 'product_cat',
      render: (category) => (
        <Tag color={getCategoryColor(category)}>{category?.toUpperCase()}</Tag>
      ),
    },
    {
      title: t('Image'),
      dataIndex: 'product_cardImg',
      key: 'product_cardImg',
      render: (image, record) => (
        <div style={{ position: 'relative' }}>
          {image ? (
            <img
              src={image}
              alt='Product'
              style={{
                width: 80,
                height: 80,
                objectFit: 'cover',
                borderRadius: '4px',
              }}
            />
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description='No image'
              style={{ width: 80, height: 80 }}
            />
          )}
          <Button
            type='primary'
            size='small'
            icon={<UploadOutlined />}
            style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              background: 'rgba(24, 144, 255, 0.8)',
            }}
            onClick={() => handleUploadClick(record.id)}
          />
        </div>
      ),
    },
    {
      title: t('Added On'),
      dataIndex: 'created_at',
      key: 'created_at',
    },
    {
      title: t('options'),
      key: 'options',
      width: 120,
      render: (_, record) => (
        <Button
          type='danger'
          icon={<DeleteOutlined />}
          onClick={() => {
            console.log('Delete button clicked for product:', {
              id: record.id,
              product_id: record.product_id,
              all_fields: record,
            });

            // Store the full record for deletion
            setSelectedProduct(record);

            // Show confirmation modal
            setIsModalVisible(true);
          }}
        >
          {t('Remove')}
        </Button>
      ),
    },
  ];

  const groupedProducts = {
    popular: products.filter((product) => product.product_cat === 'popular'),
    fashion: products.filter((product) => product.product_cat === 'fashion'),
    food: products.filter((product) => product.product_cat === 'food'),
  };

  return (
    <Card title={t('Homepage Products')}>
      {loading ? (
        <div style={{ textAlign: 'center', margin: '40px 0' }}>
          <Spin size='large' />
        </div>
      ) : (
        <>
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <Card
                title={
                  <Space>
                    <Tag color='red'>POPULAR</Tag>
                    <span>Popular Products</span>
                  </Space>
                }
                style={{ marginBottom: 16 }}
              >
                <Table
                  columns={columns}
                  dataSource={groupedProducts.popular}
                  rowKey={(record) => record.id}
                  pagination={false}
                  locale={{ emptyText: 'No products in Popular category' }}
                />
              </Card>
            </Col>

            <Col span={24}>
              <Card
                title={
                  <Space>
                    <Tag color='blue'>FASHION</Tag>
                    <span>Fashion Products</span>
                  </Space>
                }
                style={{ marginBottom: 16 }}
              >
                <Table
                  columns={columns}
                  dataSource={groupedProducts.fashion}
                  rowKey={(record) => record.id}
                  pagination={false}
                  locale={{ emptyText: 'No products in Fashion category' }}
                />
              </Card>
            </Col>

            <Col span={24}>
              <Card
                title={
                  <Space>
                    <Tag color='green'>FOOD</Tag>
                    <span>Food Products</span>
                  </Space>
                }
                style={{ marginBottom: 16 }}
              >
                <Table
                  columns={columns}
                  dataSource={groupedProducts.food}
                  rowKey={(record) => record.id}
                  pagination={false}
                  locale={{ emptyText: 'No products in Food category' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Image Upload Modal */}
          <Modal
            title='Upload Homepage Product Image'
            open={isUploadModalVisible}
            onCancel={handleUploadCancel}
            footer={[
              <Button key='back' onClick={handleUploadCancel}>
                Cancel
              </Button>,
              <Button
                key='submit'
                type='primary'
                loading={uploadLoading}
                onClick={handleHomepageImageUpload}
                disabled={!selectedFile}
              >
                Upload
              </Button>,
            ]}
          >
            <div style={{ textAlign: 'center' }}>
              <div style={{ marginBottom: 16 }}>
                <Upload
                  beforeUpload={handleFileSelect}
                  showUploadList={false}
                  accept='.jpg,.jpeg,.png,.webp'
                  maxCount={1}
                >
                  <Button icon={<UploadOutlined />}>Select Image</Button>
                </Upload>
                <div style={{ marginTop: 16 }}>
                  {previewImage ? (
                    <Card style={{ width: '100%' }}>
                      <img
                        src={previewImage}
                        alt='Preview'
                        style={{
                          maxWidth: '100%',
                          maxHeight: '200px',
                          display: 'block',
                          margin: '0 auto',
                        }}
                      />
                    </Card>
                  ) : (
                    <div
                      style={{
                        border: '1px dashed #d9d9d9',
                        borderRadius: '4px',
                        padding: '40px 20px',
                        background: '#fafafa',
                        textAlign: 'center',
                      }}
                    >
                      <p>Preview will appear here</p>
                    </div>
                  )}
                </div>
              </div>
              {uploadLoading && (
                <div style={{ marginTop: 16 }}>
                  <Spin tip='Uploading...' />
                </div>
              )}
            </div>
          </Modal>
        </>
      )}

      {/* Delete Confirmation Modal */}
      <CustomModal
        click={handleDeleteProduct}
        text='Are you sure you want to remove this product from the homepage?'
        loading={loading}
      />
    </Card>
  );
}
